import { BN, web3 } from "@project-serum/anchor";
import {
	V1_PROGRAM_ID,
	MINT_SIZE,
	MINT_LAYOUT,
	ACCOUNT_SIZE,
	MULTISIG_SIZE,
	EAccountType,
	ACCOUNT_TYPE_SIZE,
	IToken,
	ERiskLevel,
	ISignalFilters,
	SOL_MINT,
	USDC_MINT,
	USDT_MINT,
} from ".";
import { AccountInfo } from "@solana/web3.js";
import {
	Mint,
	getExtensionData,
	getExtensionTypes,
	getTransferFeeConfig,
	getTransferHook,
} from "@solana/spl-token";

import { getConfidentialTransferMint } from "@/api/token2022/confidential_transfer";
import { unpack as unpackTokenMetadata } from "@solana/spl-token-metadata";
import moment from "moment";

export function calculateInverseSwapOutput(
	beforeTokenA: any,
	beforeTokenB: any,
	inputB: any = 0
): number {
	try {
		beforeTokenA = new BN(
			beforeTokenA.toLocaleString("fullwide", { useGrouping: false })
		);
		beforeTokenB = new BN(
			beforeTokenB.toLocaleString("fullwide", { useGrouping: false })
		);
		inputB = new BN(
			(inputB || 0)
				.toFixed(0)
				.toLocaleString("fullwide", { useGrouping: false })
		);
	} catch (e) {
		console.error(`Swap::calculateInverseSwapOutput`, e);
		return 0;
	}

	const afterTokenB = beforeTokenB.add(inputB);
	const afterTokenA = beforeTokenA.mul(beforeTokenB).div(afterTokenB);
	const outputA = beforeTokenA.sub(afterTokenA);

	return Number(outputA.toString());
}

export function calculateSwapOutput(
	beforeTokenA: any,
	beforeTokenB: any,
	inputA: any = 0
): any {
	try {
		beforeTokenA = new BN(
			beforeTokenA.toLocaleString("fullwide", { useGrouping: false })
		);
		beforeTokenB = new BN(
			beforeTokenB.toLocaleString("fullwide", { useGrouping: false })
		);
		inputA = new BN(
			(inputA || 0)
				.toFixed(0)
				.toLocaleString("fullwide", { useGrouping: false })
		);
	} catch (e) {
		console.error(`calculateSwapOutput`, e);
		return 0;
	}

	const afterTokenB = beforeTokenB
		.mul(beforeTokenA)
		.div(beforeTokenA.add(inputA));

	const outputB = beforeTokenB.sub(afterTokenB);

	return outputB.toString();
}

export function convertToTokenAmount({
	amount,
	decimals,
}: {
	amount: number;
	decimals: number;
}): number {
	return amount * Math.pow(10, decimals);
}

export function convertToHumanAmount({
	amount,
	decimals,
}: {
	amount: number;
	decimals: number;
}): number {
	return amount / Math.pow(10, decimals);
}

export function getMintImageURL(mint: string): string {
	return `https://api.degencdn.com/v1/tokens/${mint}/image.jpg`;
}

export function unpackMint(
	mint: string,
	info: AccountInfo<Buffer> | null,
	programID: web3.PublicKey = V1_PROGRAM_ID
): IToken | undefined {
	if (!info) return;
	if (!info.owner.equals(new web3.PublicKey(programID))) return;
	if (info.data.length < MINT_SIZE) return;

	const rawMint = MINT_LAYOUT.decode(info.data.slice(0, MINT_SIZE));
	let tlvData = Buffer.alloc(0);
	if (info.data.length > MINT_SIZE) {
		if (info.data.length <= ACCOUNT_SIZE) return;
		if (info.data.length === MULTISIG_SIZE) return;
		if (info.data[ACCOUNT_SIZE] != EAccountType.Mint) return;
		tlvData = info.data.slice(ACCOUNT_SIZE + ACCOUNT_TYPE_SIZE);
	}

	const token: IToken = {
		amount: 0,
		decimals: rawMint.decimals,
		freezeAuthority: rawMint.freezeAuthorityOption
			? rawMint.freezeAuthority
			: null,
		image: getMintImageURL(mint),
		isInitialized: rawMint.isInitialized,
		mint,
		mintAuthority: rawMint.mintAuthorityOption ? rawMint.mintAuthority : null,
		name: "",
		owner: programID,
		programID: programID,
		address: new web3.PublicKey(mint),
		supply: rawMint.supply,
		symbol: "",
		tlvData,
		verified: false,
		extensions: [],
	};

	return token;
}

export function extensionToHumanName(ext: number): string {
	return (
		{
			1: "Transfer Fee",
			2: "Transfer Fee Amount",
			3: "Mint Close",
			4: "Confidential Transfer",
			5: "Confidential Transfer Amount",
			6: "Default Account State",
			7: "Immutable Owner",
			8: "Memo Transfer",
			9: "Soulbound",
			10: "Interest Bearing",
			11: "CPI Guard",
			12: "Permanent Delegate",
			13: "Soulbound",
			14: "Transfer Hook",
			15: "Transfer Hook Account",
			16: "Confidential Transfer",
			17: "Confidential Transfer Amount",
			18: "Metadata Pointer",
			19: "Token Metadata",
			20: "Token Group Pointer",
			21: "Token Group",
			22: "Group Member Pointer",
			nonTransferable: "Soulbound",
		}[ext] ?? ""
	);
}

export function processTokenByExtension(token: IToken): IToken {
	for (const ext of token.extensions) {
		if (ext === 1) {
			token.transferFee = getTransferFeeConfig(token as Mint);
		}
		if (ext === 4) {
			token.confidentialTransfer = getConfidentialTransferMint(token as Mint);
		}
		if (ext === 14) {
			token.transferHook = getTransferHook(token as Mint);
		}
		if (ext === 19) {
			token.tokenMetadata = unpackTokenMetadata(
				getExtensionData(ext, token.tlvData)
			);
		}
	}

	return token;
}

export function getLabelByScore(score: number): ERiskLevel {
	if (score < 1000) return "normal";
	if (score < 5000) return "warn";
	return "danger";
}

export function toScientificNotation(number: number): string {
	if (number > 0.001) return number.toFixed(2);

	const n = [
		`₀`,
		`₁`,
		`₂`,
		`₃`,
		`₄`,
		`₅`,
		`₆`,
		`₇`,
		`₈`,
		`₉`,
		`₁₀`,
		`₁₁`,
		`₁₂`,
		`₁₃`,
		`₁₄`,
		`₁₅`,
	];

	const [mantissa, exponent] = number.toExponential().split("e");
	const formattedMantissa = parseFloat(mantissa).toFixed(3);
	return `0.0${n[Math.abs(Number(exponent))]}${formattedMantissa.replace(
		".",
		""
	)}`;
}

export function timeAgo(time) {
	moment.updateLocale("en", {
		relativeTime: {
			future: "in %s",
			past: "%s ago",
			s: (number) => number + "s ago",
			ss: "%ds ago",
			m: "1m ago",
			mm: "%dm ago",
			h: "1h ago",
			hh: "%dh ago",
			d: "1d ago",
			dd: "%dd ago",
			M: "a month ago",
			MM: "%d months ago",
			y: "a year ago",
			yy: "%d years ago",
		},
	});

	const secondsElapsed = moment().diff(time, "seconds");
	const dayStart = moment("2018-01-01").startOf("day").seconds(secondsElapsed);

	if (secondsElapsed > 300) {
		return moment(time).fromNow(true);
	} else if (secondsElapsed < 60) {
		return dayStart.format("s") + "s ago";
	} else {
		return dayStart.format("m") + "m " + dayStart.format("ss") + "s ago";
	}
}

export function getAccountAmount(primitive?: any): number {
	if (!primitive) return 0;

	let amount = primitive.account.data.parsed.info.tokenAmount.amount;

	if (
		primitive.account.data.parsed.info.mint ===
			"So11111111111111111111111111111111111111112" &&
		(!amount || amount <= 0)
	) {
		amount = primitive.account.lamports ?? 0;
	}

	return Number(amount);
}

export function formatTitle(title: string): string {
	let fmtTitle = "";
	title
		.replaceAll("_", " ")
		.split(" ")
		.forEach((t) => {
			fmtTitle += " " + t.substring(0, 1).toUpperCase() + t.substring(1);
		});

	return fmtTitle;
	// return title.substring(0,1).toUpperCase() + title.substring(1).replaceAll("_", " ")
}

export function formatPrice(price: number, _exponent = true): string {
	if (!price) return "$0.00";

	if (price > 999e12) {
		// 999 trillion
		return `>$999T`;
	}

	if (price > 0.01) {
		return (
			"$" +
			price.toLocaleString("en-US", {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2,
			})
		);
	}

	if (price < 0.001 && _exponent) {
		return "$" + toScientificNotation(price);
	}

	const [_, exponent] = price.toExponential().split("e");
	return price.toLocaleString("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: Math.min(Math.abs(Number(exponent)) + 3, 9),
		maximumFractionDigits: Math.min(Math.abs(Number(exponent)) + 3, 9),
	});
}

export function formatMedPrice(price: number, _exponent = true): string {
	if (price > 999e12) {
		// 999 trillion
		return `>$999T`;
	}

	if (price < 0)
		return (
			"-$" +
			Math.abs(price).toLocaleString("en-US", {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2,
			})
		);

	return (
		"$" +
		price.toLocaleString("en-US", {
			maximumFractionDigits: 2,
			minimumFractionDigits: 2,
		})
	);
}

export function formatLargePrice(price: number, _exponent = true): string {
	if (price > 999e12) {
		// 999 trillion
		return `>$999T`;
	}

	return (
		"$" +
		price.toLocaleString("en-US", {
			maximumFractionDigits: 0,
			minimumFractionDigits: 0,
		})
	);
}

export function formatLargeAmount(price: number, decimals = 2): string {
	if (price > 999e12) {
		// 999 trillion
		return `>999T`;
	}

	const num = Number(price?.toPrecision(7) || 0);
	if (num < 1000) return `${num}`;
	if (num < 1000000) return `${(num / 1000).toFixed(decimals)}K`;
	if (num < 1000000000) return `${(num / 1000000).toFixed(decimals)}M`;
	if (num < 1000000000000) return `${(num / 1000000000).toFixed(decimals)}B`;
	if (num < 1000000000000000)
		return `${(num / 1000000000000).toFixed(decimals)}T`;
	if (num < 1000000000000000000)
		return `${(num / 1000000000000000).toFixed(decimals)}Q`;
	return `>9999T`;
}

export function formatAmount(price: number, decimals = 2): string {
	if (price > 999e12) {
		// 999 trillion
		return `>999T`;
	}

	const num = Number(price?.toPrecision(7) || 0);
	if (num < 1000) return `${num.toFixed(decimals)}`;
	if (num < 1000000) return `${(num / 1000).toFixed(decimals)}K`;
	if (num < 1000000000) return `${(num / 1000000).toFixed(decimals)}M`;
	if (num < 1000000000000) return `${(num / 1000000000).toFixed(decimals)}B`;
	if (num < 1000000000000000)
		return `${(num / 1000000000000).toFixed(decimals)}T`;
	if (num < 1000000000000000000)
		return `${(num / 1000000000000000).toFixed(decimals)}Q`;
	return `>9999T`;
}

export function convertToHumanNumber(num: number): string {
	if (num > 999e12) {
		// 999 trillion
		return `>999T`;
	}

	if (num < 1_000) return (num / 1).toFixed(2).toString();
	if (num < 1_000_000) return (num / 1_000).toFixed(2) + "k";
	if (num < 1_000_000_000) return (num / 1_000_000).toFixed(2) + "m";
	if (num < 1_000_000_000_000) return (num / 1_000_000_000).toFixed(2) + "b";
	if (num < 1_000_000_000_000_000)
		return (num / 1_000_000_000_000).toFixed(2) + "t";
	return (num / 1_000_000_000_000_000).toFixed(0) + "q";
}

export function getFluxBotToken(walletAddr: string): string {
	const auth = localStorage.getItem("fluxbot:auth");
	const walletName = localStorage.getItem("walletName");

	if (!auth || walletName !== "FluxBot") {
		return "";
	}

	const dec = JSON.parse(auth);
	const jwt = dec?.token;

	if (dec?.wallet !== walletAddr) return "";

	return `Bearer ${jwt}`;
}

export function getSupplyNumber(supply: number): bigint {
	return BigInt(supply);
}

export function Marquee(selector: string, speed: number) {
	let interval = null;
	const parentSelector = document.querySelector(selector) as HTMLElement;
	const clone = parentSelector.innerHTML;
	const firstElement = parentSelector.children[0] as HTMLElement;
	let i = 0;
	parentSelector.insertAdjacentHTML("beforeend", clone);
	parentSelector.insertAdjacentHTML("beforeend", clone);

	function startInterval() {
		interval = setInterval(function () {
			firstElement.style.marginLeft = `-${i}px`;
			if (i > firstElement.clientWidth) {
				i = 0;
			}
			i = i + speed;
		}, 0);
	}

	startInterval();

	parentSelector.addEventListener("mouseenter", () => {
		clearInterval(interval);
	});
	parentSelector.addEventListener("mouseleave", () => {
		startInterval();
	});
}

export function isDev(): boolean {
	return process.env.NODE_ENV === "development";
}

export function getFiltersByKey(
	filters: ISignalFilters = [],
	key: string
): ISignalFilters {
	return filters.filter((filter) => filter.key === key);
}

export function getMintColor(mint: string): string {
	const colors = {
		[SOL_MINT]: () => "#904CF9",
		[USDC_MINT]: () => "#2775CA",
		Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB: () => "#009393",
		"2b1kV6DkPAnxd5ixfnxCpjxmKwqjjaYmCZfHsFu24GXo": () => "#3B6FEF",
		"7GCihgDB8fe6KNjn2MYtkzZcRjQy3t9GHdC8uHYmW2hr": () => "#C6BBB8",
		EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm: () => "#9C7441",
		J3NKxxXZcnNiMjKw9hYb2K4LUxgwB6t1FtPtQVsv3KFr: () => "#D2D463",
		ED5nyyWEzpPPiWimP8vYm7sD7TD3LAt3Q3gRTWHzPJBY: () => "#8A837D",
		mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So: () => "#2C7875",
		J1toso1uCk3RLmjorhTtrVwY9HJ7X8V9yYac6Y7kGCPn: () => "#75B27B",
		A8C3xuqscfmyLrte3VmTqrAq8kgMASius9AFNANwpump: () => "#438440",
		JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN: () => "#59B49B",
		"25hAyBQfoDhfWx9ay6rarbgvWGwDdNqcHsXS3jQ3mTDJ": () => "#E73130",
		CKfatsPMUf8SkiURsDXs7eK6GWb4Jsd6UDbs7twMCWxo: () => "#F8E117",
		default: () =>
			"#" + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, "0"),
	};

	return colors[mint] ? colors[mint]() : colors.default();
}

const stableMap = {
	EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v: true,
	Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB: true,
	So11111111111111111111111111111111111111112: true,
};

export function isStableCoin(mint: string): boolean {
	return !!stableMap[mint];
}

export function isNormalScore(score: number): boolean {
	return score >= 0 && score < 2100;
}
export function isWarnScore(score: number): boolean {
	return score >= 2100 && score < 5000;
}
export function isDangerScore(score: number): boolean {
	return score >= 5000;
}

export function enableMomentumDragScroll(element) {
	const slider = element;
	let isDown = false;
	let startX;
	let scrollLeft;

	slider.addEventListener("mousedown", (e) => {
		isDown = true;
		slider.classList.add("active");
		startX = e.pageX - slider.offsetLeft;
		scrollLeft = slider.scrollLeft;
		cancelMomentumTracking();
	});

	slider.addEventListener("mouseleave", () => {
		isDown = false;
		slider.classList.remove("active");
	});

	slider.addEventListener("mouseup", () => {
		isDown = false;
		slider.classList.remove("active");
		beginMomentumTracking();
	});

	slider.addEventListener("mousemove", (e) => {
		if (!isDown) return;
		e.preventDefault();
		const x = e.pageX - slider.offsetLeft;
		const walk = (x - startX) * 0.7; //scroll-fast
		const prevScrollLeft = slider.scrollLeft;
		slider.scrollLeft = scrollLeft - walk;
		velX = slider.scrollLeft - prevScrollLeft;
	});

	// Momentum

	let velX = 0;
	let momentumID;

	slider.addEventListener("wheel", (e) => {
		cancelMomentumTracking();
	});

	function beginMomentumTracking() {
		cancelMomentumTracking();
		momentumID = requestAnimationFrame(momentumLoop);
	}
	function cancelMomentumTracking() {
		cancelAnimationFrame(momentumID);
	}
	function momentumLoop() {
		slider.scrollLeft += velX;
		velX *= 0.95;
		if (Math.abs(velX) > 0.5) {
			momentumID = requestAnimationFrame(momentumLoop);
		}
	}
}

export function checkMobile(): boolean {
	const toMatch = [
		/Android/i,
		/webOS/i,
		/iPhone/i,
		/iPad/i,
		/iPod/i,
		/BlackBerry/i,
		/Windows Phone/i,
	];

	return toMatch.some((toMatchItem) => {
		return navigator.userAgent.match(toMatchItem);
	});
}

export function getMarketCapClass(mc) {
	const vol = parseInt(mc);

	if (vol < 50_000) return "volume-normal";
	else if (vol < 100_000) return "volume-uncommon";
	else if (vol < 1_000_000) return "volume-common";
	else if (vol < 10_000_000) return "volume-rare";
	else if (vol < 100_000_000) return "volume-epic";
	else if (vol < 1_000_000_000) return "volume-legendary";
	else return "volume-mythic";
}

export function getVolumeClass(volume) {
	const vol = parseInt(volume);

	if (vol < 50_000) return "volume-normal";
	else if (vol < 100_000) return "volume-uncommon";
	else if (vol < 1_000_000) return "volume-common";
	else if (vol < 5_000_000) return "volume-rare";
	else if (vol < 10_000_000) return "volume-epic";
	else if (vol < 40_000_000) return "volume-legendary";
	else return "volume-mythic";
}

export const RESOLUTIONS_MAP = {
	"1T": 500,
	"1S": 1000,
	"5S": 5000,
	"30S": 30000,
	"1": 60000,
	"3": 180000,
	"5": 300000,
	"15": 900000,
	"30": 1.8e6,
	"60": 3.6e6,
	"180": 7.2e6,
	"240": 1.44e7,
	"1D": 8.64e7,
	"1W": 6.048e8,
	"1M": 2.628e9,
};
